import { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  Area, AreaChart, CartesianGrid
} from 'recharts';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

export default function AttendanceChart(props) {
  const { chartData, viewMode, isCountOncePerDay } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [chartOpacity, setChartOpacity] = useState(1);
  const [renderData, setRenderData] = useState(chartData);

  const scrollRef = useRef(null);

  const maxCount = chartData.length > 0 ? Math.max(...chartData.map(d => d.count)) : 0;
  const rawMax = maxCount > 0 ? maxCount * 1.15 : 10;
  const step = rawMax > 50 ? 10 : 5;
  const maximum = Math.ceil(rawMax / step) * step;

  // viewMode 전환 시 페이드 아웃 → 데이터 교체 → 페이드 인 //
  useEffect(() => {
    setChartOpacity(0);

    const timer = setTimeout(() => {
      setRenderData(chartData);
      setChartOpacity(1);
    }, 200);

    return () => clearTimeout(timer);
  }, [chartData]);

  // renderData 변경 후 스크롤 위치 리셋 //
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [renderData]);

  // 커스텀 툴팁 //
  function CustomTooltip(props) {
    const { active, payload } = props;

    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <TooltipBox>
        <TooltipLabelBox>
          <TooltipText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {data.label}
          </TooltipText>
        </TooltipLabelBox>
        <TooltipText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.subKey}>
          {data.count.toLocaleString('ko-KR')}
        </TooltipText>
      </TooltipBox>
    );
  }

  // 커스텀 커서 (세로 막대선) //
  function CustomCursor(props) {
    const { points } = props;

    if (!points || !points.length) return null;

    const x = points[0].x;
    const startY = points[0].y + 30;
    const endY = points[1].y;

    return (
      <line
        x1={x} y1={startY}
        x2={x} y2={endY}
        stroke={theme.line}
        strokeWidth={2}
      />
    );
  }

  // 커스텀 도트 //
  function CustomDot(props) {
    const { cx, cy } = props;

    if (cx === undefined || cy === undefined) return null;

    return (
      <circle cx={cx} cy={cy} r={4} fill={theme.subKey} stroke='none' />
    );
  }

  return (
    <ChartWrapper>
      <ChartScrollContainer ref={scrollRef} $opacity={chartOpacity}>
        <ChartInner $width={renderData.length > 15 ? renderData.length * 30 : undefined}>
          <ResponsiveContainer width="100%" height={310}>
            <AreaChart
              data={renderData}
              margin={{ top: 20, right: 5, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="attendanceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.subKey} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.subKey} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                horizontal={true}
                vertical={false}
                stroke={theme.line}
                strokeDasharray="none"
              />
              <XAxis
                dataKey="label"
                axisLine={{ stroke: theme.line }}
                tickLine={false}
                tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 14, fill: theme.text }}
                padding={{ left: 30, right: 30 }}
              />
              <YAxis
                domain={[0, maximum]}
                axisLine={false}
                tickLine={false}
                tick={{ fontFamily: 'NotoSansKR-Medium', fontSize: 16, fill: theme.text }}
                width={60}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={<CustomCursor />}
                position={{ y: 0 }}
                offset={-11}
                allowEscapeViewBox={{ x: false, y: true }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={theme.subKey}
                strokeWidth={2}
                fill="url(#attendanceGradient)"
                dot={<CustomDot />}
                activeDot={{ r: 4, fill: theme.subKey, stroke: theme.subKey }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartInner>
      </ChartScrollContainer>
      <ScrollbarWrapper>
        <CustomScrollbar
          key={renderData.length + '-' + (renderData[0]?.label || '') + '-' + isCountOncePerDay}
          direction="horizontal"
          scrollRef={scrollRef}
          thumbSize={100}
          showTrackAlways={false}
        />
      </ScrollbarWrapper>
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  margin-top: 20px;

  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const ChartScrollContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;

  opacity: ${props => props.$opacity};
  transition: opacity 0.1s ease-in-out;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const ChartInner = styled.div`
  min-width: ${props => props.$width ? props.$width + 'px' : '100%'};
`;

const ScrollbarWrapper = styled.div`
  width: 100%;
  height: 15px;
`;


const TooltipBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px;

  gap: 5px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  white-space: nowrap;
`;

const TooltipLabelBox = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill};
`;

const TooltipText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;
